// ===========================================
// Setup Guide Card Component
// ===========================================
// Collapsible guide for setting up third-party integrations
// Provides step-by-step instructions for obtaining API keys

import { useState } from 'react';
import {
  Card,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Link,
  Alert,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  HelpOutline as HelpIcon,
  OpenInNew as ExternalLinkIcon,
  ContentCopy as CopyIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

// ===========================================
// TYPES
// ===========================================

export interface SetupStep {
  title: string;
  description?: string;
  code?: string; // For webhook URLs or code snippets
  copyable?: boolean; // Set to true to show copy button (defaults to false for examples)
  warning?: string;
  link?: {
    url: string;
    label: string;
  };
  substeps?: string[];
}

interface SetupGuideCardProps {
  integration: 'stripe' | 'twilio' | 'mailgun' | 'slack';
  steps: SetupStep[];
  webhookUrl?: string;
  docsLink?: string;
  defaultExpanded?: boolean;
}

// ===========================================
// INTEGRATION CONFIG
// ===========================================

const INTEGRATION_CONFIG = {
  stripe: {
    name: 'Stripe',
    color: '#635BFF',
    dashboardUrl: 'https://dashboard.stripe.com',
  },
  twilio: {
    name: 'Twilio',
    color: '#F22F46',
    dashboardUrl: 'https://console.twilio.com',
  },
  mailgun: {
    name: 'Mailgun',
    color: '#F06B66',
    dashboardUrl: 'https://app.mailgun.com',
  },
  slack: {
    name: 'Slack',
    color: '#4A154B',
    dashboardUrl: 'https://api.slack.com/apps',
  },
};

// ===========================================
// COMPONENT
// ===========================================

export default function SetupGuideCard({
  integration,
  steps,
  webhookUrl,
  docsLink,
  defaultExpanded = false,
}: SetupGuideCardProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const config = INTEGRATION_CONFIG[integration];

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        elevation={0}
        sx={{
          '&:before': { display: 'none' },
          backgroundColor: 'transparent',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            '& .MuiAccordionSummary-content': {
              alignItems: 'center',
              gap: 1,
            },
          }}
        >
          <HelpIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={500}>
            Need help setting up {config.name}?
          </Typography>
          <Chip
            label="Setup Guide"
            size="small"
            variant="outlined"
            color="primary"
            sx={{ ml: 1 }}
          />
        </AccordionSummary>

        <AccordionDetails>
          <Box sx={{ pt: 1 }}>
            {/* Dashboard Link */}
            <Alert severity="info" icon={<HelpIcon />} sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight={500} gutterBottom>
                You&apos;ll need a {config.name} account to get started
              </Typography>
              <Link
                href={config.dashboardUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Open {config.name} Dashboard
                <ExternalLinkIcon fontSize="small" />
              </Link>
            </Alert>

            {/* Steps */}
            <Stack spacing={3}>
              {steps.map((step, index) => (
                <Box key={index}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    gutterBottom
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                    }}
                  >
                    <Chip
                      label={index + 1}
                      size="small"
                      color="primary"
                      sx={{ minWidth: 28, height: 24 }}
                    />
                    {step.title}
                  </Typography>

                  {step.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 5, mb: 1 }}
                    >
                      {step.description}
                    </Typography>
                  )}

                  {step.substeps && (
                    <Box sx={{ ml: 5, mb: 1 }}>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {step.substeps.map((substep, subIndex) => (
                          <li key={subIndex}>
                            <Typography variant="body2" color="text.secondary">
                              {substep}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    </Box>
                  )}

                  {step.code && (
                    <Box
                      sx={{
                        ml: 5,
                        p: 1.5,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        position: 'relative',
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            wordBreak: 'break-all',
                            pr: step.copyable ? 2 : 0,
                          }}
                        >
                          {step.code}
                        </Typography>
                        {step.copyable && (
                          <Box
                            component="button"
                            onClick={() => handleCopyToClipboard(step.code!)}
                            sx={{
                              border: 'none',
                              bgcolor: 'transparent',
                              cursor: 'pointer',
                              p: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              color: 'primary.main',
                              '&:hover': {
                                bgcolor: 'grey.200',
                                borderRadius: 1,
                              },
                            }}
                            title="Copy to clipboard"
                          >
                            <CopyIcon fontSize="small" />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}

                  {step.warning && (
                    <Alert
                      severity="warning"
                      icon={<WarningIcon />}
                      sx={{ ml: 5, mb: 1 }}
                    >
                      <Typography variant="body2">{step.warning}</Typography>
                    </Alert>
                  )}

                  {step.link && (
                    <Box sx={{ ml: 5 }}>
                      <Link
                        href={step.link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontSize: '0.875rem',
                        }}
                      >
                        {step.link.label}
                        <ExternalLinkIcon fontSize="small" />
                      </Link>
                    </Box>
                  )}

                  {index < steps.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              ))}

              {/* Webhook URL Section */}
              {webhookUrl && (
                <>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Your Webhook URL
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Use this URL when configuring webhooks in your {config.name} dashboard:
                    </Typography>
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        position: 'relative',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            wordBreak: 'break-all',
                            pr: 2,
                          }}
                        >
                          {webhookUrl}
                        </Typography>
                        <Box
                          component="button"
                          onClick={() => handleCopyToClipboard(webhookUrl)}
                          sx={{
                            border: 'none',
                            bgcolor: 'transparent',
                            cursor: 'pointer',
                            p: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'grey.200',
                              borderRadius: 1,
                            },
                          }}
                          title="Copy to clipboard"
                        >
                          <CopyIcon fontSize="small" />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

              {/* Documentation Link */}
              {docsLink && (
                <>
                  <Divider />
                  <Box>
                    <Link
                      href={docsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontWeight: 500,
                      }}
                    >
                      View full {config.name} documentation
                      <ExternalLinkIcon fontSize="small" />
                    </Link>
                  </Box>
                </>
              )}
            </Stack>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
}
